import { useState, type FormEvent } from "react";
import { errorMessage } from "../shared/api/client";
import { bffApi } from "../shared/api/endpoints";

/**
 * The credential boundary (ADR-0046 decision 6), the flow of apps/platform/sub/web's LoginPage.
 * Development only since ADR-0059: a deployed host answers `/bff/login` 404, and staff sign in
 * through RigoHR (`console-signin.tsx`, and the article gate's hand-off).
 * The form posts what the person typed to the host's own `/bff/login`; the host checks it with
 * the platform identity over a service call and answers with a `__Host-` cookie, or with
 * `mfaRequired` and a user id for the authenticator step at `/bff/totp`. No token is ever in this
 * page's hands.
 */
type Step = { kind: "password" } | { kind: "totp"; userId: string };

const fieldStyle = {
  background: "var(--background)",
  borderColor: "var(--input, var(--border))",
  color: "var(--foreground)",
};

export function SignInForm({ onSignedIn }: { onSignedIn: () => void }) {
  const [step, setStep] = useState<Step>({ kind: "password" });
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submitPassword(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const outcome = await bffApi.login(login, password);
      if (outcome.mfaRequired) {
        setStep({ kind: "totp", userId: outcome.userId });
        return;
      }
      onSignedIn();
    } catch (failure) {
      setError(errorMessage(failure) || "Sign-in failed.");
    } finally {
      setBusy(false);
      setPassword("");
    }
  }

  async function submitCode(event: FormEvent) {
    event.preventDefault();
    if (step.kind !== "totp") return;
    setBusy(true);
    setError(null);
    try {
      await bffApi.totp(step.userId, code);
      onSignedIn();
    } catch (failure) {
      setError(errorMessage(failure) || "That code was not accepted.");
    } finally {
      setBusy(false);
      setCode("");
    }
  }

  const field =
    "w-full rounded-md border px-3 py-2 text-sm outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50";
  const label = "mb-1.5 block text-[0.78rem] font-medium";
  const button =
    "flex h-10 w-full cursor-pointer items-center justify-center rounded-md px-4 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-60";
  const buttonStyle = { background: "var(--primary)", color: "var(--primary-foreground)" };

  return (
    <div className="text-left">
      {error && (
        <p
          role="alert"
          className="mb-4 rounded-md border px-3 py-2 text-sm"
          style={{
            borderColor: "color-mix(in oklch, var(--destructive) 40%, var(--border))",
            background: "color-mix(in oklch, var(--destructive) 8%, var(--background))",
            color: "var(--foreground)",
          }}
        >
          {error}
        </p>
      )}
      {step.kind === "password" ? (
        <form onSubmit={submitPassword} className="flex flex-col gap-3">
          <label className="block">
            <span className={label} style={{ color: "var(--foreground)" }}>
              Username or email
            </span>
            <input
              name="login"
              autoComplete="username"
              required
              value={login}
              onChange={(e) => setLogin(e.target.value)}
              className={field}
              style={fieldStyle}
            />
          </label>
          <label className="block">
            <span className={label} style={{ color: "var(--foreground)" }}>
              Password
            </span>
            <input
              name="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={field}
              style={fieldStyle}
            />
          </label>
          <button type="submit" disabled={busy} className={button} style={buttonStyle}>
            Sign in
          </button>
        </form>
      ) : (
        <form onSubmit={submitCode} className="flex flex-col gap-3">
          <p className="text-sm" style={{ color: "var(--muted-foreground)" }}>
            Enter the six-digit code from your authenticator app.
          </p>
          <label className="block">
            <span className={label} style={{ color: "var(--foreground)" }}>
              Authenticator code
            </span>
            <input
              name="code"
              inputMode="numeric"
              autoComplete="one-time-code"
              required
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className={`${field} tracking-widest`}
              style={fieldStyle}
            />
          </label>
          <button type="submit" disabled={busy} className={button} style={buttonStyle}>
            Continue
          </button>
        </form>
      )}
    </div>
  );
}
